// AI Service - Service layer for AI/ML related API operations

import axiosInstance from "../instance/axiosInstance";
import { AI_ENDPOINTS } from "../endpoints/ai.endpoints";
import { handleApiError } from "../utils/errorHandler";
import { SECURITY_HEADERS } from "../config/security.config";

// Lazy import to avoid circular dependency
const getStore = () => require("../../store/store").default;

// Shared sessionStorage key used by both Super Agent and Order Agent chats.
// A new session sends thread_id: null; the AI response returns a thread_id
// which we persist here and reuse on subsequent queries.
const THREAD_ID_STORAGE_KEY = "axon_ai_thread_id";

// Store the current AbortController for active AI requests
let currentAbortController = null;

/**
 * Validates that the URL uses HTTPS protocol in production environments
 * This helps ensure secure transport (related to HSTS compliance)
 * @param {string} url - The URL to validate
 * @returns {string} - The validated URL
 * @throws {Error} - If URL is not HTTPS in production
 */
const validateSecureUrl = (url) => {
  if (!url) {
    throw new Error("URL is required");
  }

  const isProduction = process.env.NODE_ENV === "production";
  const isHttps = url.startsWith("https://");
  const isLocalhost = url.includes("localhost") || url.includes("127.0.0.1");

  // In production, enforce HTTPS unless it's localhost (for local testing)
  if (isProduction && !isHttps && !isLocalhost) {
    console.warn(
      "[Security] Non-HTTPS URL detected in production. Requests should use HTTPS for security.",
    );
    // Attempt to upgrade to HTTPS
    if (url.startsWith("http://")) {
      return url.replace("http://", "https://");
    }
  }

  return url;
};

/**
 * Create a new AbortController for AI requests
 * Aborts any existing request before creating a new one
 * @returns {AbortController}
 */
export const createAbortController = () => {
  // Abort any existing request
  if (currentAbortController) {
    currentAbortController.abort();
  }
  currentAbortController = new AbortController();
  return currentAbortController;
};

/**
 * Abort the current AI request (kill switch)
 * @returns {boolean} - Returns true if a request was aborted
 */
export const abortCurrentRequest = () => {
  if (currentAbortController) {
    currentAbortController.abort();
    currentAbortController = null;
    //  console.log('[AIService] Request aborted via kill switch');
    return true;
  }
  return false;
};

/**
 * Check if there's an active AI request
 * @returns {boolean}
 */
export const hasActiveRequest = () => {
  return currentAbortController !== null;
};

/**
 * Clear the current abort controller without aborting
 */
export const clearAbortController = () => {
  currentAbortController = null;
};

export const getStoredThreadId = () => {
  try {
    const value = sessionStorage.getItem(THREAD_ID_STORAGE_KEY);
    return value && value !== "null" && value !== "undefined" ? value : null;
  } catch (e) {
    return null;
  }
};

export const setStoredThreadId = (threadId) => {
  try {
    if (threadId) {
      sessionStorage.setItem(THREAD_ID_STORAGE_KEY, threadId);
    }
  } catch (e) {
    // ignore storage errors
  }
};

export const clearStoredThreadId = () => {
  try {
    sessionStorage.removeItem(THREAD_ID_STORAGE_KEY);
  } catch (e) {
    // ignore
  }
};

// Auth token storage key (same as used in request interceptor)
const AUTH_TOKEN_STORAGE_KEY = "axon_auth_token";
const AUTH_TOKEN_EXPIRY_STORAGE_KEY = "axon_auth_token_expires_at";

/**
 * Get the stored auth token if valid
 * @returns {string|null}
 */
const getStoredAuthToken = () => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    const expiry = Number(localStorage.getItem(AUTH_TOKEN_EXPIRY_STORAGE_KEY) || "0");

    if (!token) {
      return null;
    }

    // Check if token has expired
    if (expiry && Date.now() >= expiry) {
      localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
      localStorage.removeItem(AUTH_TOKEN_EXPIRY_STORAGE_KEY);
      return null;
    }

    return token;
  } catch (e) {
    return null;
  }
};

/**
 * Build headers for AI API requests including auth token and security headers
 * @returns {Object}
 */
const buildRequestHeaders = () => {
  const headers = {
    "Content-Type": "application/json",
    ...SECURITY_HEADERS,
  };

  const token = getStoredAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

export const AIService = {
  /**
   * Send a chat message to the AI agent with streaming support.
   * The API returns Server-Sent Events (SSE) with multiple event types:
   * - review_complete: Contains the main response text
   * - images: Contains retrieved images
   * - suggestions: Contains suggested follow-up questions
   * - done: Signals the end of the stream
   *
   * @param {Object} options
   * @param {string} options.message - The user's message
   * @param {string} options.threadId - Optional thread ID for conversation continuity
   * @param {string} options.userId - User ID (required)
   * @param {string} options.orderId - Optional order ID for order-specific context
   * @param {string} options.region - Region identifier
   * @param {Object} options.resumeInput - Optional checkpoint resume data
   * @param {Object} options.statusFields - Optional order status fields
   * @param {Function} options.onChunk - Callback fired for each SSE event: ({ eventType, data }) => void
   * @param {Function} options.onComplete - Callback fired when stream completes
   * @param {Function} options.onError - Callback fired on error
   */
  sendMessage: async ({
    message,
    threadId,
    userId,
    orderId,
    region,
    resumeInput,
    statusFields,
    onChunk,
    onComplete,
    onError,
  }) => {
    if (!message && !resumeInput) {
      throw new Error("Message is required");
    }
    if (!userId || (typeof userId === "string" && userId.trim() === "")) {
      throw new Error("User ID is required");
    }

    // Get region, allowed order IDs, and selected role from Redux store
    const store = getStore();
    const state = store.getState();

    let selectedRegion = region;
    if (!selectedRegion) {
      selectedRegion = state.region?.selectedRegion?.id || "general";
    }

    // Get allowed order IDs from Redux store
    const allowedOrderIds = state.user?.allowedOrderIds || [];

    // Get selected role name from Redux store
    const selectedRole = state.user?.selectedRole;
    const roleName = selectedRole?.roleName || selectedRole?.name || "";

    // Resolve thread_id precedence:
    //   1. Caller-provided threadId (if truthy)
    //   2. Value persisted in sessionStorage from a previous AI response
    //   3. null -> signals a brand new session to the backend
    const resolvedThreadId =
      (threadId && threadId !== "null" ? threadId : null) ||
      getStoredThreadId() ||
      null;

    const payload = {
      query: message || "",
      thread_id: resolvedThreadId,
      user_id: typeof userId === "string" ? userId.trim() : userId,
      region: selectedRegion,
      allowed_order_ids: allowedOrderIds,
      role: roleName,
      ...(orderId && { order_id: orderId }),
      // When resuming a pending checkpoint, include the user-entered values so the
      // backend can resume the workflow: { resume_input: { fieldName: value, ... } }
      ...(resumeInput &&
      typeof resumeInput === "object" &&
      Object.keys(resumeInput).length > 0
        ? { resume_input: resumeInput }
        : {}),
      // Include status_fields for order chat context (order details fields)
      ...(statusFields &&
      typeof statusFields === "object" &&
      Object.keys(statusFields).length > 0
        ? { status_fields: statusFields }
        : {}),
    };

    try {
      // Create a new AbortController for this request (aborts any previous request)
      const abortController = createAbortController();

      // Use native fetch for streaming SSE support
      // AI_ENDPOINTS.CHAT already contains the full URL built from api.config
      // Validate URL uses HTTPS in production for security (HSTS compliance)
      const url = validateSecureUrl(AI_ENDPOINTS.CHAT);

      const response = await fetch(url, {
        method: "POST",
        headers : {...SECURITY_HEADERS, ...buildRequestHeaders()},
        // headers: buildRequestHeaders(),
        body: JSON.stringify(payload),
        signal: abortController.signal, // Enable abort functionality
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Check if response is a stream
      const contentType = response.headers.get("content-type") || "";
      const isStream =
        contentType.includes("text/event-stream") ||
        contentType.includes("stream") ||
        response.body;

      if (!isStream || !response.body) {
        // Fallback to regular JSON response
        const data = await response.json();
        const returnedThreadId = data?.thread_id;
        if (returnedThreadId) {
          setStoredThreadId(returnedThreadId);
        }
        if (onComplete) onComplete(data);
        return data;
      }

      // Process SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let buffer = "";
      let finalResponse = {};
      let finalThreadId = null;
      let finalTurnId = null;
      const allChunks = [];

      // Process a single SSE message
      const processSSEMessage = (message) => {
        if (!message.trim()) return;

        const lines = message.split("\n");
        let eventType = "message";
        let dataLines = [];

        for (const line of lines) {
          const trimmedLine = line.trim();

          if (!trimmedLine || trimmedLine.startsWith(":")) continue;

          if (trimmedLine.startsWith("event:")) {
            eventType = trimmedLine.slice(6).trim();
            continue;
          }

          if (trimmedLine.startsWith("data:")) {
            dataLines.push(trimmedLine.slice(5).trim());
          } else if (!trimmedLine.includes(":")) {
            // Could be continuation of data or raw data line
            dataLines.push(trimmedLine);
          }
        }

        const dataStr = dataLines.join("\n");

        if (!dataStr || dataStr === "[DONE]") {
          if (dataStr === "[DONE]" || eventType === "done") {
            if (onChunk) onChunk({ eventType: "done", data: null });
          }
          return;
        }

        try {
          let parsedData;
          try {
            parsedData = JSON.parse(dataStr);
          } catch {
            parsedData = { response: dataStr };
          }

          console.log("[SSE] Parsed data:", parsedData);

          // IMPORTANT: Check if event type is inside the JSON data (API sends "type" field)
          // The API sends: {"type": "review_complete", ...} instead of event: review_complete
          if (parsedData.type && eventType === "message") {
            eventType = parsedData.type;
          }

          const chunkInfo = { eventType, data: parsedData };
          allChunks.push(chunkInfo);

          if (parsedData.thread_id) {
            finalThreadId = parsedData.thread_id;
          }
          if (parsedData.turn_id) {
            finalTurnId = parsedData.turn_id;
          }

          finalResponse = { ...finalResponse, ...parsedData };

          // Immediately call onChunk - UI handles display
          if (onChunk) {
            onChunk(chunkInfo);
          }
        } catch (parseError) {
          console.error("[SSE] Parse error:", parseError);
        }
      };

      // Read stream chunk by chunk
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        const chunk = decoder.decode(value, { stream: true });

        buffer += chunk;

        // Try to process complete SSE messages
        // SSE messages are separated by double newlines OR we look for complete JSON objects

        // Method 1: Split by \n\n (standard SSE)
        let messages = buffer.split("\n\n");
        buffer = messages.pop() || "";

        // If no complete messages found, try to find complete data lines
        if (messages.length === 0 && buffer.includes("data:")) {
          // Try to extract complete "data: {...}" lines
          const dataMatch = buffer.match(/data:\s*(\{[^]*?\})\s*(?=\n|$)/);
          if (dataMatch) {
            const fullMatch = dataMatch[0];
            const jsonStr = dataMatch[1];

            // Verify it's complete JSON
            try {
              JSON.parse(jsonStr);
              // It's valid JSON, process it
              processSSEMessage(fullMatch);
              buffer = buffer.slice(
                buffer.indexOf(fullMatch) + fullMatch.length,
              );
            } catch {
              // Incomplete JSON, wait for more data
            }
          }
        }

        for (const message of messages) {
          if (message.trim()) {
            processSSEMessage(message);
          }
        }
      }

      // Process remaining buffer
      if (buffer.trim()) {
        processSSEMessage(buffer.trim());
      }

      // Persist thread_id
      if (finalThreadId) {
        setStoredThreadId(finalThreadId);
      }

      const aggregatedResponse = {
        chunks: allChunks,
        thread_id: finalThreadId,
        turn_id: finalTurnId,
        ...finalResponse,
      };

      // Clear the abort controller on successful completion
      clearAbortController();

      if (onComplete) onComplete(aggregatedResponse);

      return aggregatedResponse;
    } catch (error) {
      // Clear the abort controller
      clearAbortController();

      // Handle abort error gracefully - don't treat as failure
      if (error.name === "AbortError") {
        // console.log("[AIService] Request was aborted via kill switch");
        const abortResponse = {
          aborted: true,
          message: "Request was cancelled",
        };
        if (onComplete) onComplete(abortResponse);
        return abortResponse;
      }
      if (onError) onError(error);
      throw handleApiError(error);
    }
  },

  /**
   * Abort the current AI request (kill switch)
   * This is an alias for the exported abortCurrentRequest function
   */
  abortRequest: abortCurrentRequest,

  // Get file data triage for field mappings
  getFileDataTriage: async (orderId) => {
    try {
      const response = await axiosInstance.get(
        AI_ENDPOINTS.GET_FILE_DATA_TRIAGE(orderId),
      );
      return response?.data || response;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Save draft data for field mappings
  saveDraftData: async (payload) => {
    try {
      const response = await axiosInstance.post(
        AI_ENDPOINTS.SAVE_DRAFT_DATA,
        payload,
      );
      return response?.data || response;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Confirm mappings for field mappings
  confirmMappings: async (payload) => {
    try {
      const response = await axiosInstance.post(
        AI_ENDPOINTS.CONFIRM_MAPPINGS,
        payload,
      );
      return response?.data || response;
    } catch (error) {
      throw handleApiError(error);
    }
  },
};

export default AIService;
